// What a catalogue row armours. Every buying surface asks this: Browse to label a row
// Armour, a location sheet to decide the row can go there at all.

import { covers } from "../vendor/web";
import type { BakedRow } from "./catalogue";
import type { Stall } from "./quality";

interface DrBonus {
  locations?: unknown;
  amount?: unknown;
  specialization?: unknown;
}

/** One of this character's hit locations, as the body table resolved it. */
export interface BodyLocation {
  id: string;
  name: string;
}

/** The DR-granting features on a row. A row with one appears on the body diagram. */
export function drBonuses(row: BakedRow): DrBonus[] {
  const features = row["features"];
  if (!Array.isArray(features)) return [];
  return features.filter(
    (feature) => (feature as { type?: unknown }).type === "dr_bonus",
  ) as DrBonus[];
}

export function grantsDr(row: BakedRow): boolean {
  return drBonuses(row).length > 0;
}

export function coversLocation(row: BakedRow, location: string): boolean {
  return drBonuses(row).some((bonus) => reaches(bonus, location));
}

function reaches(bonus: DrBonus, location: string): boolean {
  const named = bonus.locations;
  return (
    Array.isArray(named) &&
    named.some((where) => typeof where === "string" && covers(where, location))
  );
}

/** The rows this character could put on that hit location, in catalogue order. */
export function offeredAt(stalls: Stall[], location: string): Stall[] {
  return stalls.filter((stall) => coversLocation(stall.stock, location));
}

/** The hit locations at least one of these rows armours, in body-table order. */
export function coveredLocations(
  stalls: Stall[],
  locations: readonly BodyLocation[],
): BodyLocation[] {
  return locations.filter((location) =>
    stalls.some((stall) => coversLocation(stall.stock, location.id)),
  );
}

/** The DR the row adds, at every amount it adds it, resolved onto this character's body. */
export interface DrLine {
  amount: number;
  covers: BodyLocation[];
}

/** A damage type the item's DR moves against, printed beside the plain number. */
export interface DrCaveat {
  amount: number;
  against: string;
}

export interface DrReading {
  lines: DrLine[];
  caveats: DrCaveat[];
}

export function drReading(
  row: BakedRow,
  locations: readonly BodyLocation[],
): DrReading | null {
  const bonuses = drBonuses(row);
  if (bonuses.length === 0) return null;

  const byAmount = new Map<number, BodyLocation[]>();
  const caveats: DrCaveat[] = [];
  for (const bonus of bonuses) {
    const amount = typeof bonus.amount === "number" ? bonus.amount : 0;
    if (typeof bonus.specialization === "string") {
      caveats.push({ amount, against: bonus.specialization });
      continue;
    }
    const reached = locations.filter((location) => reaches(bonus, location.id));
    byAmount.set(amount, [...(byAmount.get(amount) ?? []), ...reached]);
  }

  const lines = [...byAmount.entries()]
    .map(([amount, reached]) => ({
      amount,
      covers: locations.filter((location) => reached.includes(location)),
    }))
    .filter((line) => line.covers.length > 0);
  return lines.length === 0 ? null : { lines, caveats };
}
